import * as React from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { AppThemeMode, getAppTheme } from './palette';

export type ThemePreference = 'system' | AppThemeMode;

const STORAGE_KEY = 'theme-mode';
const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';
const THEME_COLOR = {
    light: '#5e5373',
    dark: '#121212',
};

type ThemeModeContextValue = {
    preference: ThemePreference;
    resolvedMode: AppThemeMode;
    setPreference: (preference: ThemePreference) => void;
};

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(undefined);

const readStoredPreference = (): ThemePreference => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
};

export const ThemeModeProvider = (props: { children: React.ReactNode }) => {
    const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
    const [systemPrefersDark, setSystemPrefersDark] = useState(
        () => window.matchMedia(DARK_MEDIA_QUERY).matches
    );

    useEffect(() => {
        const mediaQuery = window.matchMedia(DARK_MEDIA_QUERY);
        const onChange = (event: MediaQueryListEvent) => setSystemPrefersDark(event.matches);
        mediaQuery.addEventListener('change', onChange);
        return () => mediaQuery.removeEventListener('change', onChange);
    }, []);

    const resolvedMode: AppThemeMode = preference === 'system'
        ? (systemPrefersDark ? 'dark' : 'light')
        : preference;

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', resolvedMode);
        document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[resolvedMode]);
    }, [resolvedMode]);

    const setPreference = (next: ThemePreference) => {
        setPreferenceState(next);
        localStorage.setItem(STORAGE_KEY, next);
    };

    const theme = useMemo(() => getAppTheme(resolvedMode), [resolvedMode]);

    const value = useMemo(
        () => ({ preference, resolvedMode, setPreference }),
        [preference, resolvedMode]
    );

    return (
        <ThemeModeContext.Provider value={value}>
            <MuiThemeProvider theme={theme}>
                <StyledThemeProvider theme={theme}>
                    <CssBaseline />
                    {props.children}
                </StyledThemeProvider>
            </MuiThemeProvider>
        </ThemeModeContext.Provider>
    );
};

export const useThemeMode = (): ThemeModeContextValue => {
    const context = useContext(ThemeModeContext);
    if (!context) {
        throw new Error('useThemeMode must be used within a ThemeModeProvider');
    }
    return context;
};
